import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button, Flex, Stack, Text } from '@chakra-ui/react';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import { useCreateApplicationNote } from '~/core/shared/api/middle-office/application';
import { FormField, Input, Wysiwyg } from '~/core/shared/components';
import UnsavedModal from '~/core/shared/components/UnsavedModal.component';
import { useFormResolver } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { NoteCreateFormValues } from '~/core/shared/types';

type NoteCreateProps = {
  documentId: string;
  documentDefinition: string;
  cancel: () => void;
};

const validationSchema: yup.ObjectSchema<NoteCreateFormValues> = yup.object({
  content: yup.string().trim().required(),
  subject: yup.string().trim().required(),
});

export const NoteCreate: FC<NoteCreateProps> = ({
  cancel,
  documentId,
  documentDefinition,
}) => {
  const unsavedChangesModalApi = useOpenClose();
  const defaultValues: NoteCreateFormValues = {
    content: '',
    subject: '',
  };

  // TODO: @haxxxton refactor to use form blocks
  const resolver = useFormResolver<NoteCreateFormValues>(validationSchema);
  const form = useForm<NoteCreateFormValues>({
    defaultValues,
    resolver,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const { createNote } = useCreateApplicationNote(
    documentId,
    documentDefinition,
  );

  const onSubmit = useEventHandler(
    async (formValues: NoteCreateFormValues): Promise<void> => {
      await createNote(formValues);
      form.reset(defaultValues);
      cancel();
    },
  );

  const handleCloseAndReset = useEventHandler(() => {
    form.reset(defaultValues);
    unsavedChangesModalApi.onClose();
    cancel();
  });

  const handleClose = useEventHandler(() => {
    if (form.formState.isDirty) {
      unsavedChangesModalApi.onOpen();
    } else {
      handleCloseAndReset();
    }
  });

  return (
    <Flex className="privateNote privateNoteSidebar" direction="column">
      <Text my={5}>Store internal updates to an application</Text>
      <FormProvider {...form}>
        <Stack as="form" gap={4} onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            component={Input}
            label="Subject"
            mb={5}
            name="subject"
            required
            variant="outline"
          />

          <FormField
            borderColor="gray.300"
            component={Wysiwyg}
            defaultValue=""
            height="60vh"
            label="Message text"
            name="content"
            required
            size="md"
            variant="outline"
          />

          <Stack direction="row" justifyContent="end" mt={3}>
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={handleClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              colorPalette="primary"
              fontSize="md"
              fontWeight="bold"
              type="submit"
              variant="solid"
            >
              Save private note
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
      <UnsavedModal
        close={handleCloseAndReset}
        modalApi={unsavedChangesModalApi}
      />
    </Flex>
  );
};
